import {memo, createElement} from 'react';

import URI from 'urijs';
import {Helmet} from 'react-helmet';

import {websiteUrl, basedir, facebookAppId, isJustEat} from '~/shared/config';
import {getCurrentLocation} from '~/shared/router';
import {SupportedLanguages, DEFAULT_LANGUAGE_KEY, SupportedLanguageType} from '~/shared/consts/commonConsts';
import {changeLanguagePrefixToUrlOrPath, getLocalizationService} from '~/shared/services/localisationService';

const getHrefLangKey = (languageKey: SupportedLanguageType['key']) => `hrefLang-${languageKey}`;

interface MetaHeadersProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  canonicalPath?: string;
  keywords?: string;
  imageUrl?: string;
  hreflangs?: Partial<Record<SupportedLanguageType['key'], string>>;
}

const JE_CDN_IMAGE_URL = 'https://cdn.just-eat.co.il/justeat-il-spa-content/Facebook_Placeholder.png';
const TENBIS_CDN_IMAGE_URL = 'https://cdn.10bis.co.il/tenbis-content/Facebook_Placeholder.png';

const MetaHeaders = memo(
  ({title, description, canonicalUrl: canonicalUrlFromArgs, canonicalPath, keywords, imageUrl, hreflangs}: MetaHeadersProps) => {
    const modifiedImage = imageUrl || isJustEat ? JE_CDN_IMAGE_URL : TENBIS_CDN_IMAGE_URL;

    const {pathname, hostname} = getCurrentLocation();
    const {currentLanguageKey} = getLocalizationService();
    const showNoIndexMetaTag = !hostname.startsWith('www.');

    const canonicalUrl =
      canonicalUrlFromArgs ||
      URI(
        `${websiteUrl}${basedir}${currentLanguageKey === DEFAULT_LANGUAGE_KEY ? '' : `${currentLanguageKey}/`}${
          canonicalPath || pathname
        }`,
      )
        .normalize()
        .toString();

    return (
      <>
        {showNoIndexMetaTag && (
          <Helmet>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
        )}
        {title && (
          <Helmet>
            <title key="title">{title}</title>
            <meta key="meta_og_title" property="og:title" content={title} />
          </Helmet>
        )}
        {description && (
          <Helmet>
            <meta key="meta_description" name="description" content={description} />
            <meta key="meta_og_description" property="og:description" content={description} />
          </Helmet>
        )}
        {canonicalUrl && (
          <Helmet>
            <meta key="meta_og_url" property="og:url" content={canonicalUrl} />
            <link key="canonical" rel="canonical" href={canonicalUrl} />
          </Helmet>
        )}
        {keywords && (
          <Helmet>
            <meta key="meta_keywords" name="keywords" content={keywords} />
          </Helmet>
        )}
        <Helmet>
          <meta key="meta_og_image" property="og:image" content={modifiedImage} />
          <meta key="meta_og_image_width" property="og:image:width" content={imageUrl ? '75' : '1200'} />
          <meta key="meta_og_image_height" property="og:image:height" content={imageUrl ? '75' : '630'} />
          <meta key="meta_fb_app_id" property="fb:app_id" content={String(facebookAppId)} />
        </Helmet>
        {hreflangs &&
          createElement(
            Helmet,
            null,
            ...Object.entries(hreflangs).map(([languageKey, hrefForLanguage]) => {
              return (
                <link key={getHrefLangKey(languageKey as SupportedLanguageType['key'])} href={hrefForLanguage} rel="alternate" hrefLang={languageKey} />
              );
            }),
          )}
        {!hreflangs &&
          createElement(
            Helmet,
            null,
            ...SupportedLanguages.map(({key: languageKey}) => {
              const href = changeLanguagePrefixToUrlOrPath(canonicalUrl, languageKey);
              return <link key={getHrefLangKey(languageKey)} href={href} rel="alternate" hrefLang={languageKey} />;
            }),
          )}
      </>
    );
  },
);

export default MetaHeaders;
